import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import makeStyles from '@mui/styles/makeStyles';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import { Link as RouterLink } from 'react-router-dom';
import { useIntl } from 'react-intl';
import {
  UserLicense,
  getRedirectPath,
  testCloudLicenses,
  UNLIMITED_EXECUTIONS,
} from './utils';
import { OrganizationFeature, UsagePlan, SubscriptionSource } from '../../models';
import { useQuery, resolvePath, sso as ssoPath } from '../../routes';
import ProductIcon from './ProductIcon';
import { useConfig } from '../../config';
import { testCloudDashboard } from '../../layout/routes';

const useStyles = makeStyles(theme => ({
  card: {
    minHeight: theme.spacing(14.5),
    boxShadow: 'none',
    padding: theme.spacing(2, 2.5),
    '&:hover': {
      boxShadow: '0px 6px 8px rgba(209, 209, 209, 0.6)',
    },
  },
  productName: {
    marginLeft: theme.spacing(1),
    fontWeight: theme.typography.fontWeightBold,
  },
  productIcon: {
    width: theme.spacing(3),
  },
  licenseCount: {
    marginLeft: theme.spacing(4),
    marginTop: theme.spacing(0.75),
  },
  numberLicences: {
    fontWeight: theme.typography.fontWeightBold,
  },
  rootCard: {
    paddingRight: theme.spacing(1),
    paddingTop: theme.spacing(1.5),
  },
}));

// Licenses sub-components:
interface LicenseProps {
  license: UserLicense;
  orgId: string | null;
}
const License = (props: LicenseProps) => {
  const { license, orgId } = props;
  const classes = useStyles();
  const { config } = useConfig();
  const intl = useIntl();

  return (
    <Grid item lg={4} md={6} xs={12} className={classes.rootCard}>
      <Link
        underline="none"
        key={license.feature}
        component={RouterLink}
        to={testCloudLicenses.includes(license.feature)
          ? `${testCloudDashboard.path}?orgId=${orgId}`
          : resolvePath(
            ssoPath,
            undefined,
            { redirect_uri: getRedirectPath(license.feature, Number(orgId), config) },
          )}
      >
        <Card className={classes.card}>
          {/* license header */}
          <Grid container alignItems="center">
            <ProductIcon feature={license.feature} />
            <Typography className={classes.productName} variant="subtitle2">
              { OrganizationFeature.getFeatureFullNameWithoutKatalon(
                license.feature,
                license.source,
              ) }
            </Typography>
          </Grid>
          <Typography variant="subtitle2" className={classes.licenseCount}>
            <span className={classes.numberLicences}>
              { intl.formatNumber(license.quota) }
              {' '}
            </span>
            { testCloudLicenses.includes(license.feature)
              ? intl.formatMessage({ id: 'home.product_summary.testcloud.session' }, { count: license.quota })
              : intl.formatMessage({ id: 'home.product_summary.license' }, { count: license.quota })}
          </Typography>
        </Card>
      </Link>
    </Grid>

  );
};
const TestOpsLicense = (props: LicenseProps) => {
  const { license, orgId } = props;
  const classes = useStyles();
  const { config } = useConfig();
  const intl = useIntl();
  const isUnlimited = license.source === SubscriptionSource.TRIAL
    && (license.usagePlan !== UsagePlan.PLATFORM
      || license.quota >= UNLIMITED_EXECUTIONS);

  return (
    <Grid item lg={4} md={6} xs={12} className={classes.rootCard}>
      <Link
        underline="none"
        key={license.feature}
        component={RouterLink}
        to={resolvePath(
          ssoPath,
          undefined,
          { redirect_uri: getRedirectPath(license.feature, Number(orgId), config) },
        )}
      >
        <Card className={classes.card}>
          {/* license header */}
          <Grid container alignItems="center">
            <ProductIcon feature={license.feature} />
            <Typography className={classes.productName} variant="subtitle2">
              {`${UsagePlan.getPlanFullName(license.usagePlan)} ${!license.source ? intl.formatMessage({ id: 'home.free' }) : license.source === SubscriptionSource.TRIAL ? intl.formatMessage({ id: 'home.trial' }) : ''}`.trim()}
            </Typography>
          </Grid>
          <Typography variant="subtitle2" className={classes.licenseCount}>
            <span className={classes.numberLicences}>
              {isUnlimited ? intl.formatMessage({ id: 'home.testops.unlimited' }) : intl.formatNumber(license.quota)}
              {' '}
            </span>
            { intl.formatMessage({ id: 'home.testops.executions' }, { count: license.quota }) }
          </Typography>
          <Typography variant="subtitle2" className={classes.licenseCount}>
            <span className={classes.numberLicences}>
              { intl.formatNumber(license.projects || 0) }
              {' '}
            </span>
            { intl.formatMessage({ id: 'home.testops.projects' }, { count: license.projects }) }
          </Typography>
        </Card>
      </Link>
    </Grid>
  );
};
// --------------------------- End licenses sub-components ---------------------------
interface ProductsSummaryProps {
  licenses: UserLicense[];
  testOpsLicense: UserLicense;
}

const ProductSummary = (props: ProductsSummaryProps) => {
  const { licenses, testOpsLicense } = props;
  const { get } = useQuery();
  const { config } = useConfig();
  const orgId = get('orgId');

  return (
    <Grid container>
      { licenses.map(it => <License key={it.feature} license={it} orgId={orgId} />)}
      {
        config?.onpremise ? (
          testOpsLicense.usagePlan !== UsagePlan.COMMUNITY
          && testOpsLicense.source !== SubscriptionSource.TRIAL) && (
          <TestOpsLicense key={testOpsLicense.feature} license={testOpsLicense} orgId={orgId} />
        ) : (<TestOpsLicense key={testOpsLicense.feature} license={testOpsLicense} orgId={orgId} />)
      }

    </Grid>
  );
};
export default ProductSummary;
